import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";

interface RichTextEditorProps {
  initialContent?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  height?: string;
  className?: string;
  showToolbar?: boolean;
  toolbarOptions?: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    highlight?: boolean;
    headings?: boolean;
    lists?: boolean;
    alignment?: boolean;
    links?: boolean;
    images?: boolean;
  };
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
  initialContent = "", 
  onChange,
  placeholder = "Enter content here...",
  height = "400px",
  className = "",
  showToolbar = true,
  toolbarOptions = {
    bold: true,
    italic: true,
    underline: true,
    highlight: true,
    headings: true,
    lists: true,
    alignment: true,
    links: true,
    images: true,
  }
}) => {
  const [content, setContent] = useState(initialContent);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline cursor-pointer',
        },
      }),
      Image,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setContent(html);
      onChange?.(html);
    },
  });

  useEffect(() => {
    if (editor && initialContent !== content) {
      editor.commands.setContent(initialContent);
      setContent(initialContent);
    }
  }, [initialContent, editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Enter URL');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className={`border border-gray-300 rounded-md bg-white shadow-sm ${className}`}>
      {/* Toolbar */}
      {showToolbar && (
        <div className="border-b border-gray-200 bg-gray-50 p-2">
          <div className="flex flex-wrap gap-1">
            {/* Text Formatting */}
            {(toolbarOptions.bold || toolbarOptions.italic || toolbarOptions.underline || toolbarOptions.highlight) && (
              <div className="flex items-center gap-1 border-r border-gray-300 pr-2">
                {toolbarOptions.bold && (
                  <button
                    className={`h-8 w-8 p-0 flex items-center justify-center ${
                      editor.isActive("bold") ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                    } rounded-md transition-colors`}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    title="Bold"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
                      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
                    </svg>
                  </button>
                )}
                {toolbarOptions.italic && (
                  <button
                    className={`h-8 w-8 p-0 flex items-center justify-center ${
                      editor.isActive("italic") ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                    } rounded-md transition-colors`}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    title="Italic"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="4" x2="10" y2="4"></line>
                      <line x1="14" y1="20" x2="5" y2="20"></line>
                      <line x1="15" y1="4" x2="9" y2="20"></line>
                    </svg>
                  </button>
                )}
                {toolbarOptions.underline && (
                  <button
                    className={`h-8 w-8 p-0 flex items-center justify-center ${
                      editor.isActive("underline") ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                    } rounded-md transition-colors`}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    title="Underline"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3"></path>
                      <line x1="4" y1="21" x2="20" y2="21"></line>
                    </svg>
                  </button>
                )}
                {toolbarOptions.highlight && (
                  <button
                    className={`h-8 w-8 p-0 flex items-center justify-center ${
                      editor.isActive("highlight") ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                    } rounded-md transition-colors`}
                    onClick={() => editor.chain().focus().toggleHighlight().run()}
                    title="Highlight"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11H1l8-8 8 8h-8v8z"></path>
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Headings */}
            {toolbarOptions.headings && (
              <div className="flex items-center gap-1 border-r border-gray-300 pr-2">
                <button
                  className={`h-8 px-2 flex items-center justify-center text-sm font-medium ${
                    editor.isActive("heading", { level: 1 }) ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                  } rounded-md transition-colors`}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  title="Heading 1"
                >
                  H1
                </button>
                <button
                  className={`h-8 px-2 flex items-center justify-center text-sm font-medium ${
                    editor.isActive("heading", { level: 2 }) ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                  } rounded-md transition-colors`}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  title="Heading 2"
                >
                  H2
                </button>
                <button
                  className={`h-8 px-2 flex items-center justify-center text-sm font-medium ${
                    editor.isActive("heading", { level: 3 }) ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                  } rounded-md transition-colors`}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  title="Heading 3"
                >
                  H3
                </button>
              </div>
            )}

            {/* Lists */}
            {toolbarOptions.lists && (
              <div className="flex items-center gap-1 border-r border-gray-300 pr-2">
                <button
                  className={`h-8 w-8 p-0 flex items-center justify-center ${
                    editor.isActive("bulletList") ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                  } rounded-md transition-colors`}
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  title="Bullet List"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6"></line>
                    <line x1="8" y1="12" x2="21" y2="12"></line>
                    <line x1="8" y1="18" x2="21" y2="18"></line>
                    <line x1="3" y1="6" x2="3.01" y2="6"></line>
                    <line x1="3" y1="12" x2="3.01" y2="12"></line>
                    <line x1="3" y1="18" x2="3.01" y2="18"></line>
                  </svg>
                </button>
                <button
                  className={`h-8 w-8 p-0 flex items-center justify-center ${
                    editor.isActive("orderedList") ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                  } rounded-md transition-colors`}
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  title="Numbered List"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="10" y1="6" x2="21" y2="6"></line>
                    <line x1="10" y1="12" x2="21" y2="12"></line>
                    <line x1="10" y1="18" x2="21" y2="18"></line>
                    <path d="M4 6h1v4"></path>
                    <path d="M4 10h2"></path>
                    <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"></path>
                  </svg>
                </button>
              </div>
            )}

            {/* Text Alignment */}
            {toolbarOptions.alignment && (
              <div className="flex items-center gap-1 border-r border-gray-300 pr-2">
                <button
                  className={`h-8 w-8 p-0 flex items-center justify-center ${
                    editor.isActive({ textAlign: 'left' }) ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                  } rounded-md transition-colors`}
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  title="Align Left"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="17" y1="10" x2="3" y2="10"></line>
                    <line x1="21" y1="6" x2="3" y2="6"></line>
                    <line x1="21" y1="14" x2="3" y2="14"></line>
                    <line x1="17" y1="18" x2="3" y2="18"></line>
                  </svg>
                </button>
                <button
                  className={`h-8 w-8 p-0 flex items-center justify-center ${
                    editor.isActive({ textAlign: 'center' }) ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                  } rounded-md transition-colors`}
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  title="Align Center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="10" x2="6" y2="10"></line>
                    <line x1="21" y1="6" x2="3" y2="6"></line>
                    <line x1="21" y1="14" x2="3" y2="14"></line>
                    <line x1="18" y1="18" x2="6" y2="18"></line>
                  </svg>
                </button>
                <button
                  className={`h-8 w-8 p-0 flex items-center justify-center ${
                    editor.isActive({ textAlign: 'right' }) ? "bg-blue-100 text-blue-700" : "hover:bg-gray-100"
                  } rounded-md transition-colors`}
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  title="Align Right"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="21" y1="10" x2="7" y2="10"></line>
                    <line x1="21" y1="6" x2="3" y2="6"></line>
                    <line x1="21" y1="14" x2="3" y2="14"></line>
                    <line x1="21" y1="18" x2="7" y2="18"></line>
                  </svg>
                </button>
              </div>
            )}

            {/* Links and Media */}
            {(toolbarOptions.links || toolbarOptions.images) && (
              <div className="flex items-center gap-1">
                {toolbarOptions.links && (
                  <button
                    className="h-8 w-8 p-0 flex items-center justify-center hover:bg-gray-100 rounded-md transition-colors"
                    onClick={addLink}
                    title="Add Link"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                  </button>
                )}
                {toolbarOptions.images && (
                  <button
                    className="h-8 w-8 p-0 flex items-center justify-center hover:bg-gray-100 rounded-md transition-colors"
                    onClick={addImage}
                    title="Add Image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21,15 16,10 5,21"></polyline>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Editor Content */}
      <div 
        className="overflow-y-auto" 
        style={{ height }}
      >
        <EditorContent 
          editor={editor}
          className="prose prose-sm max-w-none py-4 px-6 min-h-full focus:outline-none" 
        />
      </div>
    </div>
  );
};

export default RichTextEditor; 